// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  InvoiceFilter,
  getInvoices,
  serializeInvoiceFilter,
  serializeInvoiceSummaryScope,
} from '@/billing/api/get-invoices';
import { TWO_MINUTES_MS } from '@/utilities/time-constants';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

interface UseInvoicesOptions {
  filter?: InvoiceFilter;
}

export function useInvoices(options: UseInvoicesOptions = {}) {
  const filter = options.filter ?? { pageSize: 20 };
  const serializedFilter = serializeInvoiceFilter(filter);
  // Summary-scope is a subset of the filter that only includes customer,
  // project, date ranges, amount range, and aging buckets — not status,
  // search, sort, or page. Consumers use this to know when the
  // summary-affecting filters changed.
  const summaryScope = serializeInvoiceSummaryScope(filter);

  const { data, isLoading, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['invoices', 'list', serializedFilter],
    queryFn: () => getInvoices(filter),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchInterval: TWO_MINUTES_MS,
  });

  // `invoices` and `totalCount` are `null` while the first fetch is in
  // flight. Consumers must treat `null` as "not yet known" and render a
  // skeleton — surfacing `[]` / `0` here fabricates an empty result set and
  // causes the listing to flash an empty state, "0 items" export label, or
  // "1-0 of 0" footer before the real data arrives.
  return {
    invoices: data?.results ?? null,
    totalCount: data?.totalCount ?? null,
    summary: data?.summary ?? null,
    amountRange: data?.amountRange ?? null,
    summaryScope,
    isLoading,
    isFetching,
    isPlaceholderData,
  };
}
